<script setup lang="ts">
import { useFirebaseMessagingStore } from '@/stores/firebaseMessaging'
import { usePWAStore } from '@/stores/pwa'
import { pwaConfig, pwaOnDesktop } from '@/utils/pwaTools'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useClassesStore } from '@/stores/classes'
import { isSupported } from 'firebase/messaging'
import { useServiceWorkerRegistered } from '@/utils/registerSW'
import { bowser } from '@/utils/pwaTools'
import { useCustomerClassSchedulesStore } from '@/stores/customerClassSchedules'
import { useAuthStore } from '@/stores/auth'

const pwaStore = usePWAStore()
const { deferredPrompt, showInstallPromptOnAndroid, homePagePromptIsDismissed } =
  storeToRefs(pwaStore)

const firebaseMessagingStore = useFirebaseMessagingStore()
const {
  permission,
  registrationToken,
  classReminderGlobalFcm,
  classReminderGlobalSms,
  messaging,
  enabledAndLoaded,
} = storeToRefs(firebaseMessagingStore)
const classesStore = useClassesStore()
const customerClassSchedulesStore = useCustomerClassSchedulesStore()

const upcomingClassReminderClassNames = computed(() => {
  void customerClassSchedulesStore.enrolledClassScheduleIds
  void customerClassSchedulesStore.enrollmentNotificationConfigByScheduleId
  return classesStore.classesWithTestClasses
    .filter((c) => customerClassSchedulesStore.isUpcomingReminderEnabledForClass(c.id))
    .map((c) => c.name ?? c.id)
})

const enrollmentReminderIntervalsDebug = computed(() => {
  void customerClassSchedulesStore.enrollmentNotificationConfigByScheduleId
  const lines: string[] = []
  for (const scheduleId of customerClassSchedulesStore.enrolledClassScheduleIds) {
    const cfg = customerClassSchedulesStore.getEnrollmentNotificationConfig(scheduleId)
    const fcmOn = customerClassSchedulesStore.isChannelReminderOn(scheduleId, 'fcm')
    const smsOn = customerClassSchedulesStore.isChannelReminderOn(scheduleId, 'sms')
    lines.push(
      `${scheduleId}: fcm=${cfg.fcm.enabled}[${cfg.fcm.intervals.join(',')}] sms=${cfg.sms.enabled}[${cfg.sms.intervals.join(',')}] activeFcm=${fcmOn} activeSms=${smsOn}`,
    )
  }
  return lines.length ? lines : ['(no schedule enrollments)']
})

const authStore = useAuthStore()
const { allowPWA } = storeToRefs(authStore)

const {
  isServiceWorkerRegistered,
  swRegistration,
  swInstalling,
  swWaiting,
  swActive,
  swController,
} = useServiceWorkerRegistered()

const firestoreMessagingSupported = ref<boolean | null>(null)
isSupported().then((supported) => {
  firestoreMessagingSupported.value = !!supported
})

const items = computed(() => {
  return {
    // Browser Info
    ['allowPWA']: allowPWA.value,
    ['browserInfo.browser.name']: bowser.browser.name,
    ['browserInfo.browser.version']: bowser.browser.version,
    ['browserInfo.os.name']: bowser.os.name,
    ['browserInfo.os.version']: bowser.os.version,
    ['browserInfo.platform.model']: bowser.platform.model,
    ['browserInfo.platform.type']: bowser.platform.type,
    ['browserInfo.platform.vendor']: bowser.platform.vendor,
    // PWA Store
    ['userAgent']: navigator.userAgent,
    ['window.crossOriginIsolated']: window.crossOriginIsolated,
    ['deferredPrompt']: deferredPrompt.value,
    ['showInstallPromptOnAndroid']: showInstallPromptOnAndroid.value,
    ['homePagePromptIsDismissed']: homePagePromptIsDismissed.value,
    // Firebase Messaging
    ['permission']: permission.value,
    ['FCM registration token']: registrationToken.value?.slice(-16) || 'No token',
    ['classReminderGlobalFcm']: classReminderGlobalFcm.value,
    ['classReminderGlobalSms']: classReminderGlobalSms.value,
    ['upcomingClassPushReminders']: upcomingClassReminderClassNames.value,
    ['enrollmentReminderIntervals']: enrollmentReminderIntervalsDebug.value,
    // PWA Config
    ['pwaNotificationsAvailable']: pwaConfig.pwaNotificationsAvailable,
    ['enabledAndLoaded']: enabledAndLoaded.value,
    ['firestoreMessagingSupported']: firestoreMessagingSupported.value,
    ['firebaseMessagingStore messaging']: !!messaging.value,
    ['serviceWorkersRegistered']: isServiceWorkerRegistered.value,
    // Service Worker States
    ['SW Registration']: !!swRegistration.value,
    ['SW Installing state']: swInstalling.value ? `${swInstalling.value.state}` : 'None',
    ['SW Waiting state']: swWaiting.value ? `${swWaiting.value.state}` : 'None',
    ['SW Active state']: swActive.value ? `${swActive.value.state}` : 'None',
    ['SW Controller state']: swController.value ? `${swController.value.state}` : 'None',
    ['promptChangeBrowserAndroid']: pwaConfig.promptChangeBrowserAndroid,
    ['promptChangeBrowserIOS']: pwaConfig.promptChangeBrowserIOS,
    ['promptSafariInstructionsIOS']: pwaConfig.promptSafariInstructionsIOS,
    ['promptInstallOnMobileInstructions']: pwaConfig.promptInstallOnMobileInstructions,
    ['isAndroidChrome']: pwaConfig.other.isAndroidChrome,
    ['isAndroidBrowserNotChrome']: pwaConfig.other.isAndroidBrowserNotChrome,
    ['isIOSBrowserSafari']: pwaConfig.other.isIOSBrowserSafari,
    ['isIOSBrowserNotSafari']: pwaConfig.other.isIOSBrowserNotSafari,
    ['isDesktop']: pwaConfig.other.isDesktop,
    ['isChrome']: pwaConfig.other.isChrome,
    ['runningInStandaloneMode']: pwaConfig.runningInStandaloneMode,
    ['displayMode']: pwaConfig.displayMode,
    ['supportPwaInstallation']: pwaConfig.supportPwaInstallation,
    ['supportPwaInstallationIOS']: pwaConfig.supportPwaInstallationIOS,
    ['VITE_PWA_ON_DESKTOP']: pwaOnDesktop,
  }
})

const textColorBasedOnValue = (value: unknown) => {
  return {
    'text-green-700': value === true,
    'text-red-500': value === false,
    'text-gray-500': value === null,
    'text-cyan-500': value === 'activating',
    'text-blue-500': value === 'activated' || value === 'active',
    'text-yellow-500': value === 'installing' || value === 'waiting',
    'text-orange-500': value === 'installed',
    'text-purple-500': value === 'redundant' || value === 'parsed' || value === 'none',
  }
}
</script>

<template>
  <div>
    <table class="w-full">
      <thead>
        <tr>
          <th>Key</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr class="border-b border-gray-600">
          <td>{{ new Date().toLocaleTimeString() }}</td>
        </tr>
        <tr
          v-for="(value, key, index) in items"
          :key="key"
          class="border-b border-gray-600"
          :class="{ 'bg-gray-200': index % 2 === 0 }"
        >
          <td class="gap-2" :class="textColorBasedOnValue(value)">
            {{ key }}
          </td>
          <td class="gap-2 pl-2" :class="textColorBasedOnValue(value)">
            <template v-if="Array.isArray(value)">
              <ul>
                <li v-for="item in value" :key="item">{{ item }}</li>
              </ul>
            </template>
            <template v-else>
              {{ value }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
@media (display-mode: browser) {
  #installInstructions {
    display: block;
  }
}
</style>
